import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import throttle from 'lodash.throttle';
import debounce from 'lodash.debounce';

import Routes from './Routes';
import Ripple from './Ripple';
import MobileMenu from './MobileMenu';
import Hamburger from './Hamburger';
import Logo from '../assets/svg/spwordmark.svg';

import {
  media,
  mediaValues,
  colors,
  rgbColors,
  animation,
  circleColorMap
} from '../styles/constants';
import { padding } from '../styles/padding';
import { navLink } from '../styles/link';

const { shortAnimationDuration, transitionTimingFn } = animation;

const SCROLL_THROTTLE_PERIOD = 100;
const RESIZE_DEBOUNCE_PERIOD = 200;

const NavContainer = styled.nav`
  display: flex;
  flex-direction: column;
  top: 0;
  left: 0;
  right: 0;
  position: fixed;

  ${props =>
    props.isMenuOpen &&
    css`
      height: 100%;

      @media (min-width: ${media.tabletPortrait}) {
        height: auto;
      }
    `}

  padding: 3rem ${padding.smallPhone.horizontal};

  @media (min-width: ${media.phone}) {
    padding: 3rem ${padding.phone.horizontal};
  }

  @media (min-width: ${media.tabletPortrait}) {
    padding: 4.5rem ${padding.tabletPortrait.horizontal} 3rem ${
  padding.tabletPortrait.horizontal
};
  }

  @media (min-width: ${media.tabletLandscape}) {
    padding: 4.5rem ${padding.tabletLandscape.horizontal} 3rem ${
  padding.tabletLandscape.horizontal
};
  }

  @media (min-width: ${media.desktop}) {
    padding: 6rem ${padding.desktop.horizontal} 3rem ${
  padding.desktop.horizontal
};
  }

  @media (min-width: ${media.bigDesktop}) {
    padding: 7rem ${padding.bigDesktop.horizontal} 3rem ${
  padding.bigDesktop.horizontal
};
  }

  /* [Style] Tweak regular nav animation here */
  ${props =>
    props.hideOnScroll &&
    css`
      transition-property: transform, background-color;
      transition-duration: ${shortAnimationDuration}ms;
      transition-timing-function: ${transitionTimingFn};

      &.hidden {
        transform: translate(0, -18rem);
      }

      &.static {
        background-color: transparent;
      }

      &.floating {
        background-color: ${rgbColors.lightGrey};
        transform: translate(0, 0);

        && a {
          transition-property: color;
          transition-duration: ${shortAnimationDuration}ms;
          transition-timing-function: ${transitionTimingFn};

          color: ${props =>
            props.isMenuOpen ? colors.white : colors.graphite};

          &:after {
            transition-property: background-color;
            transition-duration: ${shortAnimationDuration}ms;
            transition-timing-function: ${transitionTimingFn};
            background-color: ${props =>
              props.isMenuOpen ? colors.white : colors.graphite};
          }

          &:focus,
          &:hover,
          &.selected {
            color: ${props =>
              props.isMenuOpen ? colors.white : colors.graphite};
          }
        }

        && button span {
          transition-property: background-color;
          transition-duration: ${shortAnimationDuration}ms;
          transition-timing-function: ${transitionTimingFn};
          background-color: ${props =>
            props.isMenuOpen ? colors.white : colors.graphite};
        }

        svg {
          transition-property: fill;
          transition-duration: ${shortAnimationDuration}ms;
          transition-timing-function: ${transitionTimingFn};
          fill: ${props => (props.isMenuOpen ? colors.white : colors.graphite)};
        }
      }
    `}


  a,
  .logo {
    margin-right: 5rem;
    margin-top: 0.2rem;
    ${navLink}
  }

  svg {
    fill: ${colors.white};
  }
`;

const NarrowNav = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  height: 2.8rem;
  z-index: 1;

  @media (min-width: ${media.tabletPortrait}) {
    display: none;
  }

  button:nth-child(2) {
    margin-left: auto;
  }
`;

const WideNav = styled.div`
  display: none;

  @media (min-width: ${media.tabletPortrait}) {
    display: flex;
  }
`;

class Nav extends Component {
  static propTypes = {
    hideOnScroll: PropTypes.bool,
    colorIndex: PropTypes.number
  };

  static defaultProps = {
    colorIndex: -1
  };

  state = {
    isMenuOpen: false,
    status: 'static',
    oldScroll: -1,
    canTransitionNav: false
  };

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.props.colorIndex !== nextProps.colorIndex ||
      this.props.hideOnScroll !== nextProps.hideOnScroll ||
      this.state.isMenuOpen !== nextState.isMenuOpen ||
      this.state.status !== nextState.status ||
      this.state.canTransitionNav !== nextState.canTransitionNav
    );
  }

  changeBodyOverflow = (invert = false) =>
    setTimeout(
      function() {
        if (document) {
          document.documentElement.style.overflow =
            this.state.isMenuOpen && !invert ? 'hidden' : 'auto';
        }
      }.bind(this),
      shortAnimationDuration
    );

  handleMenuClick = () => {
    this.setState(
      prevState => ({ isMenuOpen: !prevState.isMenuOpen }),
      this.changeBodyOverflow
    );
  };

  handleHomeScroll = throttle(
    () => {
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      if (scrollY !== 0 && !this.state.canTransitionNav) {
        this.setState({
          canTransitionNav: true
        });
      }
    },
    SCROLL_THROTTLE_PERIOD,
    { leading: true, trailing: false }
  ).bind(this);

  handleScroll = throttle(
    () => {
      const { status, oldScroll, isMenuOpen } = this.state;
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;

      if (scrollY > 20 && !isMenuOpen) {
        if (scrollY < oldScroll && status !== 'floating') {
          this.setState({
            status: 'floating'
          });
        }

        if (scrollY > oldScroll && status !== 'hidden') {
          this.setState({
            status: 'hidden'
          });
        }
      } else if (status !== 'static') {
        this.setState({
          status: 'static'
        });
      }

      this.setState({ oldScroll: scrollY });
    },
    SCROLL_THROTTLE_PERIOD,
    { leading: true, trailing: true }
  ).bind(this);

  handleNavResize = () => {
    const windowWidth =
      window.innerWidth || document.documentElement.clientWidth;

    if (windowWidth >= mediaValues.tabletPortrait) {
      this.changeBodyOverflow(true);
    }
  };

  handleResize = debounce(this.handleNavResize, RESIZE_DEBOUNCE_PERIOD, {
    leading: false,
    trailing: true
  }).bind(this);

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    window.addEventListener(
      'scroll',
      this.props.hideOnScroll ? this.handleScroll : this.handleHomeScroll
    );
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener(
      'scroll',
      this.props.hideOnScroll ? this.handleScroll : this.handleHomeScroll
    );
  }

  render() {
    const { hideOnScroll, colorIndex } = this.props;
    const { isMenuOpen, status, canTransitionNav } = this.state;

    const shouldShowOnUpwardsScroll = isMenuOpen ? true : hideOnScroll;

    return (
      <NavContainer
        isMenuOpen={isMenuOpen}
        hideOnScroll={shouldShowOnUpwardsScroll}
        className={status ? status : ''}
        style={{
          backgroundColor:
            colorIndex > -1 && canTransitionNav
              ? circleColorMap[colorIndex]
              : hideOnScroll
              ? 'auto'
              : 'transparent',
          ...(!hideOnScroll && {
            transitionProperty: 'background-color',
            transitionDelay: `${animation.delayNav}ms`,
            transitionDuration: `${animation.navColorDuration}ms`,
            transitionTimingFunction: 'ease-in-out'
          })
        }}
      >
        <WideNav>
          <Link to="/" aria-label="Home">
            <Logo />
          </Link>
          <Routes />
        </WideNav>
        <NarrowNav>
          <Link to="/" aria-label="Home">
            <Logo />
          </Link>
          <Hamburger isMenuOpen={isMenuOpen} onClick={this.handleMenuClick} />
        </NarrowNav>
        <Ripple isMenuOpen={isMenuOpen} />
        <MobileMenu isMenuOpen={isMenuOpen} onClick={this.handleMenuClick} />
      </NavContainer>
    );
  }
}

export default Nav;
